import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, Users, ShoppingBag, UtensilsCrossed } from 'lucide-react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import TableServiceOrderModal from '@/components/TableServiceOrder/TableServiceOrderModal';
import MenuImagesModal from '@/components/MenuImages';
import TranslateInline, { TranslateBar } from '@/components/TranslateInline';

interface TableGroupChatProps {
  bookingId: string;
  tableName?: string;
  /** Branch that owns this table — enables the "Order items" panel (browse catalogue + send order). */
  branchId?: string;
  onClose: () => void;
}

interface GroupMessage {
  id: string;
  bookingId: string;
  senderType: 'customer' | 'organizer';
  senderId: string;
  senderName: string;
  senderProfilePic?: string | null;
  messageText: string;
  createdAt: string;
}

interface GroupMember {
  customerId: string;
  userId?: string | null;
  name: string;
  chairNumber: number;
  isMainBooker: boolean;
}

/**
 * Table GROUP chat (customer side): a shared conversation with the organizer and every other
 * checked-in customer at the table. Only available while the customer is checked in — once they
 * check out the backend removes access, so this panel shows a "closed" state and no input.
 */
const TableGroupChat: React.FC<TableGroupChatProps> = ({ bookingId, tableName, branchId, onClose }) => {
  const [showOrder, setShowOrder] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [translateOn, setTranslateOn] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (branchId) {
      apiService.getTranslationAvailableForBranch(branchId)
        .then((a) => { if (a) setTranslateOn(true); }).catch(() => { /* hidden */ });
    }
  }, [branchId]);

  const myUserId = useMemo(() => {
    const { accessToken } = apiService.getTokens();
    return accessToken ? getUserIdFromToken(accessToken) : null;
  }, []);
  const myCustomerId = useMemo(
    () => members.find((m) => m.userId && m.userId === myUserId)?.customerId ?? null,
    [members, myUserId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const status = await apiService.getTableGroupChatStatus(bookingId).catch(() => null);
        if (!cancelled && status?.data) {
          setEnabled(status.data.enabled);
          setMembers(status.data.members || []);
          if (!status.data.enabled) {
            setClosedMessage('This table group chat is no longer available.');
          }
        }
        const res = await apiService.getTableGroupChatMessages(bookingId, 1, 100);
        if (!cancelled && res?.data) {
          setMessages(res.data.messages || []);
          setEnabled(res.data.enabled);
        }
      } catch (e) {
        if (!cancelled) setClosedMessage(e instanceof Error ? e.message : 'This table group chat is not available.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  // Live updates on the shared table channel.
  useEffect(() => {
    const unsub = subscribeToChannel(
      `table_group:${bookingId}`,
      ['table_group_message:new'] as unknown as readonly string[],
      (event, data) => {
        if (event === 'table_group_message:new') {
          setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
        }
      }
    );
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [bookingId]);

  // Poll fallback: while this panel is open, re-fetch the table group messages every 5s in case the
  // Ably `table_group_message:new` event was missed during a reconnect gap. Reuses the same loader
  // call used on open (getTableGroupChatMessages) and merges by id — appends only messages we don't
  // already have — so replays never duplicate. Cleared on unmount (panel close). Additive: the Ably
  // subscription and send path above are untouched.
  useEffect(() => {
    let cancelled = false;
    const iv = setInterval(async () => {
      try {
        const res = await apiService.getTableGroupChatMessages(bookingId, 1, 100);
        if (cancelled) return;
        const fetched = ((res?.data?.messages || []) as GroupMessage[]);
        if (!fetched.length) return;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const newOnes = fetched.filter((m) => m?.id && !ids.has(m.id));
          return newOnes.length ? [...prev, ...newOnes] : prev;
        });
      } catch { /* non-fatal */ }
    }, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [bookingId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await apiService.sendTableGroupChatMessage(bookingId, text);
      const msg = res?.data;
      if (msg) setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      setInput('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const time = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Users size={18} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>Table Group Chat</div>
              <div style={subLabel}>{tableName || 'Everyone at your table'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {branchId && (
              <button type="button" onClick={() => setShowMenu(true)} style={menuBtn}>
                <UtensilsCrossed size={15} /> Menu
              </button>
            )}
            {branchId && enabled && (
              <button type="button" onClick={() => setShowOrder(true)} style={orderBtn}>
                <ShoppingBag size={15} /> Order
              </button>
            )}
            <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
          </div>
        </div>

        {translateOn && <div style={{ padding: '8px 12px 0' }}><TranslateBar enabled={translateOn} convId={bookingId} /></div>}

        <div style={body}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem' }}>Loading…</p>
          ) : messages.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>
              {closedMessage || 'No messages yet. Say hello 👋'}
            </p>
          ) : (
            messages.map((m) => {
              const own = m.senderType === 'customer' && !!myCustomerId && m.senderId === myCustomerId;
              const isOrganizer = m.senderType === 'organizer';
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: own ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{ ...bubble, ...(own ? bubbleOwn : isOrganizer ? bubbleOrg : bubbleOther) }}>
                    {!own && <div style={{ ...senderName, color: isOrganizer ? '#b45309' : '#4f46e5' }}>{isOrganizer ? `${m.senderName} (Organizer)` : m.senderName}</div>}
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.messageText}</div>
                  </div>
                  <TranslateInline text={m.messageText} enabled={translateOn} branchId={branchId} isIncoming={!own} convId={bookingId} />
                  <div style={timeLabel}>{time(m.createdAt)}</div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        {error && <div style={errorBox}>{error}</div>}

        {enabled ? (
          <div style={footer}>
            <input
              style={inputStyle}
              placeholder="Message your table…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            <button type="button" style={{ ...sendBtn, opacity: input.trim() && !sending ? 1 : 0.6 }} disabled={!input.trim() || sending} onClick={handleSend}>
              <Send size={16} />
            </button>
          </div>
        ) : (
          <div style={{ ...footer, justifyContent: 'center', color: '#6b7280', fontSize: '0.85rem' }}>
            {closedMessage || 'This group chat is closed.'}
          </div>
        )}
      </div>

      {showOrder && branchId && (
        <TableServiceOrderModal branchId={branchId} bookingId={bookingId} tableName={tableName} onClose={() => setShowOrder(false)} />
      )}

      {showMenu && branchId && (
        <MenuImagesModal branchId={branchId} branchName={tableName} onClose={() => setShowMenu(false)} />
      )}
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1200 };
const orderBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9999, padding: '6px 12px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' };
const menuBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 9999, padding: '6px 12px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' };
const panel: React.CSSProperties = { background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: 'min(520px, 100vw)', height: 'min(640px, 88vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 -6px 30px rgba(0,0,0,0.25)' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', gap: 8 };
const subLabel: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '12px 14px', background: '#f9fafb' };
const bubble: React.CSSProperties = { maxWidth: '80%', padding: '7px 10px', borderRadius: 12, fontSize: '0.92rem', lineHeight: 1.35 };
const bubbleOwn: React.CSSProperties = { background: '#2563eb', color: '#fff', borderBottomRightRadius: 4 };
const bubbleOther: React.CSSProperties = { background: '#fff', color: '#111827', border: '1px solid #e5e7eb', borderBottomLeftRadius: 4 };
const bubbleOrg: React.CSSProperties = { background: '#fffbeb', color: '#111827', border: '1px solid #fde68a', borderBottomLeftRadius: 4 };
const senderName: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 700, marginBottom: 2 };
const timeLabel: React.CSSProperties = { fontSize: '0.65rem', color: '#9ca3af', margin: '2px 4px 0' };
const footer: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderTop: '1px solid #e5e7eb' };
const inputStyle: React.CSSProperties = { flex: 1, border: '1px solid #d1d5db', borderRadius: 9999, padding: '9px 14px', fontSize: '0.92rem', outline: 'none' };
const sendBtn: React.CSSProperties = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: '50%', width: 38, height: 38, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' };
const errorBox: React.CSSProperties = { background: '#fef2f2', color: '#b91c1c', borderTop: '1px solid #fecaca', padding: '7px 12px', fontSize: '0.82rem' };

export default TableGroupChat;
